import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { formatCurrency } from '@/utils/currencyFormat';
import { formatLocalDate } from '@/utils/dateUtils';
import { exportToPDF, exportToExcel, exportToCSV } from '@/utils/exportUtils';
import { showToast } from '@/lib/custom-toast';
import { 
  ArrowUpRight, 
  ArrowDownRight, 
  PiggyBank, 
  Search, 
  Check, 
  Pencil, 
  Copy, 
  Trash2,
  Package,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  FileText,
  FileSpreadsheet,
  X,
  Calendar,
  Wallet,
  Gem,
  Sparkles,
  Lock
} from 'lucide-react';

// ─── Ítem de transacción con swipe ──────────────────────
const SwipeableTransaction = ({
  t,
  isFirst,
  hintTriggered,
  categories,
  accounts,
  transactionBalancesMap,
  isRevealed,
  onReveal,
  onCloseReveal,
  onEdit,
  onDuplicate,
  onDelete
}) => {
  const startX = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const [showItems, setShowItems] = useState(false);
  const isDragging = useRef(false);
  const isHinting = useRef(false);
  const REVEAL_WIDTH = 180; // px para 3 botones

  const typeConfig = {
    income: { icon: ArrowUpRight, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    expense: { icon: ArrowDownRight, color: 'text-rose-600', bg: 'bg-rose-50' },
    saving: { icon: PiggyBank, color: 'text-blue-600', bg: 'bg-blue-50' }
  }[t.type];
  const Icon = typeConfig.icon;

  const category = categories.find(c => c.id === t.category_id);

  // Swipe hint automático para el primer ítem al entrar
  useEffect(() => {
    if (!isFirst || !hintTriggered) return;
    isHinting.current = true;
    const timeout1 = setTimeout(() => {
      setOffsetX(-REVEAL_WIDTH);
    }, 400);
    const timeout2 = setTimeout(() => {
      setOffsetX(0);
      isHinting.current = false;
    }, 1800);
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      isHinting.current = false;
    };
  }, [isFirst, hintTriggered]);

  // Si otra transacción se abrió o se cerró externamente, resetear offset
  useEffect(() => {
    if (!isRevealed && !isDragging.current && !isHinting.current) {
      setOffsetX(0);
    }
  }, [isRevealed]);

  // Auto-cierre por inactividad tras 4.5 segundos si está abierta
  useEffect(() => {
    if (!isRevealed) return;
    const timer = setTimeout(() => {
      setOffsetX(0);
      onCloseReveal();
    }, 4500);
    return () => clearTimeout(timer);
  }, [isRevealed, onCloseReveal]);

  const onTouchStart = e => {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
  };

  const onTouchMove = e => {
    if (startX.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    if (dx < 0) {
      setOffsetX(Math.max(dx, -REVEAL_WIDTH));
    } else if (isRevealed) {
      setOffsetX(Math.min(0, -REVEAL_WIDTH + dx));
    }
  };

  const onTouchEnd = () => {
    isDragging.current = false;
    if (Math.abs(offsetX) > REVEAL_WIDTH / 2) {
      setOffsetX(-REVEAL_WIDTH);
      onReveal();
    } else {
      setOffsetX(0);
      if (isRevealed) onCloseReveal();
    }
    startX.current = null;
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (isRevealed) {
      setOffsetX(0);
      onCloseReveal();
    } else {
      onEdit(t);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-3xl bg-transparent" style={{ touchAction: 'pan-y' }}>
      {/* Botones detrás - Ocultos cuando offsetX es 0 para evitar sangrado en bordes */}
      <div 
        className="absolute right-0 top-0 bottom-0 flex items-stretch overflow-hidden rounded-3xl"
        style={{
          opacity: offsetX < 0 ? 1 : 0,
          pointerEvents: offsetX < 0 ? 'auto' : 'none',
          transition: 'opacity 0.15s ease'
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setOffsetX(0); onCloseReveal(); onEdit(t); }}
          className="w-[60px] bg-blue-600 flex flex-col items-center justify-center gap-1 active:bg-blue-700 transition-colors"
        >
          <Pencil className="w-4 h-4 text-white" />
          <span className="text-white text-[9px] font-bold">Editar</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setOffsetX(0); onCloseReveal(); onDuplicate && onDuplicate(t); }}
          className="w-[60px] bg-indigo-600 flex flex-col items-center justify-center gap-1 active:bg-indigo-700 transition-colors"
        >
          <Copy className="w-4 h-4 text-white" />
          <span className="text-white text-[9px] font-bold">Duplicar</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setOffsetX(0); onCloseReveal(); onDelete(t); }}
          className="w-[60px] bg-rose-500 flex flex-col items-center justify-center gap-1 active:bg-rose-600 transition-colors"
        >
          <Trash2 className="w-4 h-4 text-white" />
          <span className="text-white text-[9px] font-bold">Borrar</span>
        </button>
      </div>

      {/* Contenido deslizable */}
      <div
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isDragging.current ? 'none' : 'transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleCardClick}
      >
        <Card className="p-3.5 bg-white dark:bg-slate-800 border-none shadow-sm rounded-2xl">
          <div className="flex items-start gap-3">
            {/* Icono */}
            <div className={`w-10 h-10 rounded-xl ${typeConfig.bg} flex items-center justify-center shrink-0 mt-0.5`}>
              <Icon className={`w-5 h-5 ${typeConfig.color}`} />
            </div>

            {/* Contenido principal */}
            <div className="flex-1 min-w-0">
              {/* Fila 1: Título y Monto */}
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                  {t.description || 'Sin descripción'}
                </p>
                <p className={`text-sm font-black shrink-0 ${typeConfig.color}`}>
                  {t.type === 'income' ? '+' : '-'}{formatCurrency(t.amount)}
                </p>
              </div>

              {/* Fila 2: Categoría, Artículos y Nombre de Cuenta */}
              <div className="flex items-center justify-between gap-2 mt-0.5 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                    {category?.name || 'Sin categoría'}
                  </p>
                  {t.items_breakdown?.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowItems(!showItems);
                      }}
                      className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-bold rounded-full flex items-center gap-1 active:scale-95 transition-all shadow-xs"
                    >
                      <Package className="w-3 h-3 text-indigo-600" />
                      <span>{t.items_breakdown.length} artículos</span>
                      {showItems ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                    </button>
                  )}
                </div>
                {t.account_id && (
                  <p className="text-[10px] font-medium text-slate-400 truncate max-w-[140px]">
                    {accounts.find(a => a.id === t.account_id)?.name}
                  </p>
                )}
              </div>

              {/* Fila 3: Saldo anterior y saldo después responsivo y limpio */}
              {(() => {
                const bal = transactionBalancesMap?.[t.id] || 
                            (t.previous_balance !== null && t.previous_balance !== undefined && t.new_balance !== null && t.new_balance !== undefined
                              ? { prev: t.previous_balance, next: t.new_balance }
                              : null);
                if (!bal) return null;
                const acc = accounts.find(a => a.id === t.account_id);
                const isCredit = acc?.type === 'credit_card';
                const isExpense = t.type === 'expense';
                const isIncome = t.type === 'income';
                const amt = parseFloat(t.amount) || 0;

                let prevVal = bal.prev;
                let nextVal = bal.next ?? bal.curr;

                if (isCredit && prevVal !== undefined && !isNaN(prevVal)) {
                  const prevDebt = Math.abs(prevVal);
                  prevVal = prevDebt;
                  if (t.is_balance_adjustment || /ajuste de saldo/i.test(t.description || '')) {
                    nextVal = nextVal !== undefined && !isNaN(nextVal) ? Math.abs(nextVal) : prevDebt;
                  } else if (t.is_breakdown || (t.is_retention && t.retention_status === 'released')) {
                    nextVal = prevDebt;
                  } else if (isExpense) {
                    nextVal = prevDebt + amt; // Compra AUMENTA la deuda
                  } else if (isIncome) {
                    nextVal = Math.max(0, prevDebt - amt); // Abono REDUCE la deuda
                  } else {
                    nextVal = prevDebt;
                  }
                }

                const nextColor = isExpense ? 'text-rose-500/90 dark:text-rose-400/90' : 'text-emerald-600/90 dark:text-emerald-400/90';

                return (
                  <div className="mt-2 px-2.5 py-1 bg-slate-50/70 dark:bg-slate-800/40 rounded-xl border border-slate-100 dark:border-slate-700/50 flex items-center justify-between text-[10px] gap-1 shadow-2xs">
                    <div className="flex items-center gap-1 text-slate-400 dark:text-slate-500 truncate">
                      <span className="font-normal opacity-80">Ant:</span>
                      <span className="font-medium text-slate-600 dark:text-slate-400">
                        {prevVal !== undefined && !isNaN(prevVal) ? formatCurrency(isCredit ? Math.abs(prevVal) : prevVal) : 'N/D'}
                      </span>
                    </div>
                    <span className="text-slate-300 dark:text-slate-600 font-normal shrink-0">→</span>
                    <div className="flex items-center gap-1 truncate">
                      <span className="font-normal text-slate-400 dark:text-slate-500 opacity-80">Después:</span>
                      <span className={`font-semibold ${nextColor}`}>
                        {nextVal !== undefined && !isNaN(nextVal) ? formatCurrency(isCredit ? Math.abs(nextVal) : nextVal) : 'N/D'}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Desglose desplegado sin editar */}
          {showItems && t.items_breakdown?.length > 0 && (
            <div 
              onClick={(e) => e.stopPropagation()} 
              className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 space-y-1.5"
            >
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                Artículos de esta compra ({t.items_breakdown.length})
              </p>
              <div className="space-y-1">
                {t.items_breakdown.map((item, idx) => (
                  <div key={item.id || idx} className="flex items-center justify-between text-xs py-1 px-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <div className="flex items-center gap-1.5 truncate mr-2">
                      <span className="text-slate-700 dark:text-slate-300 font-medium truncate">{item.name}</span>
                      {item.category_name && (
                        <span className="text-[9px] px-1.5 py-0.2 bg-slate-200/80 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded font-bold shrink-0">
                          {item.category_name}
                        </span>
                      )}
                    </div>
                    <span className="font-bold text-slate-900 dark:text-white shrink-0">${parseFloat(item.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ─── Componente BottomSheet con Gesto Drag-Down para Móvil ───
const MobileBottomSheet = ({ isOpen, onClose, children }) => {
  const [dragY, setDragY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const isFromHandle = useRef(false);
  const contentRef = useRef(null);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setDragY(0);
    }, 220);
  }, [onClose]);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    const isAtTop = !contentRef.current || contentRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    const isAtTop = !contentRef.current || contentRef.current.scrollTop <= 0;
    if (diff > 0 && (isFromHandle.current || isAtTop)) {
      setDragY(diff);
    } else if (diff < 0) {
      isDragging.current = false;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const threshold = isFromHandle.current ? 70 : 180;
    if (dragY > threshold) {
      handleClose();
    } else {
      setDragY(0);
    }
    startY.current = 0;
    isFromHandle.current = false;
  };

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs transition-opacity duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleClose}
    >
      <div
        ref={contentRef}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          transform: isClosing
            ? 'translateY(100%)'
            : (dragY > 0 ? `translate3d(0, ${dragY}px, 0)` : undefined),
          transition: isDragging.current ? 'none' : 'transform 0.26s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        className="w-full sm:max-w-md bg-white rounded-t-[2.5rem] sm:rounded-3xl p-5 sm:p-6 space-y-4 max-h-[88vh] overflow-y-auto shadow-2xl pb-12 sm:pb-6 will-change-transform animate-in slide-in-from-bottom-8 duration-300"
      >
        {/* Handle táctil interactivo con touchAction: none para arrastre suave */}
        <div
          onTouchStart={(e) => handleTouchStart(e, true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
          className="flex justify-center pt-0 pb-3 -mt-2 -mx-4 cursor-grab active:cursor-grabbing select-none"
        >
          <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
        </div>

        {typeof children === 'function' ? children({ close: handleClose }) : children}
      </div>
    </div>,
    document.body
  );
};

// ─── Lista principal con swipe hint ──────────────────────
const MobileTransactions = ({ transactions = [], categories = [], accounts = [], user = null, onEdit, onDuplicate, onDelete }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [hintTriggered, setHintTriggered] = useState(false);
  const [revealedId, setRevealedId] = useState(null);

  // Estados de Filtros Avanzados
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [dateFilter, setDateFilter] = useState('all'); // 'all' | 'this-month' | 'last-month' | 'this-year' | 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Estado de Modal Exportación
  const [showExportModal, setShowExportModal] = useState(false);

  const isPro = user?.subscription_tier === 'pro' || user?.role === 'admin';
  const userName = user?.nickname || user?.username || 'Usuario';

  const filterChips = [
    { id: 'all', label: 'Todos' },
    { id: 'income', label: 'Ingresos' },
    { id: 'expense', label: 'Gastos' },
    { id: 'saving', label: 'Ahorros' },
  ];

  // Conteo de filtros avanzados activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (dateFilter !== 'all') count += 1;
    if (selectedAccount !== 'all') count += 1;
    if (selectedCategory !== 'all') count += 1;
    return count;
  }, [dateFilter, selectedAccount, selectedCategory]);

  const resetFilters = () => {
    setDateFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSelectedAccount('all');
    setSelectedCategory('all');
  };

  // Validación de fechas
  const isDateInFilter = (dateStr) => {
    if (dateFilter === 'all') return true;
    if (!dateStr) return false;
    const txDate = new Date(dateStr);
    const now = new Date();

    if (dateFilter === 'this-month') {
      return txDate.getFullYear() === now.getFullYear() && txDate.getMonth() === now.getMonth();
    }
    if (dateFilter === 'last-month') {
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return txDate.getFullYear() === lastMonthDate.getFullYear() && txDate.getMonth() === lastMonthDate.getMonth();
    }
    if (dateFilter === 'this-year') {
      return txDate.getFullYear() === now.getFullYear();
    }
    if (dateFilter === 'custom') {
      const datePart = dateStr.split('T')[0];
      if (customStartDate && datePart < customStartDate) return false;
      if (customEndDate && datePart > customEndDate) return false;
      return true;
    }
    return true;
  };

  // Calcular saldos antes y después por cuenta para cada movimiento
  const transactionBalancesMap = useMemo(() => {
    if (!transactions || transactions.length === 0 || !accounts || accounts.length === 0) return {};
    const map = {};

    const byAccount = {};
    for (const t of transactions) {
      if (!t.account_id) continue;
      if (!byAccount[t.account_id]) byAccount[t.account_id] = [];
      byAccount[t.account_id].push(t);
    }

    for (const [accId, txList] of Object.entries(byAccount)) {
      const acc = accounts.find(a => a.id === accId);
      if (!acc) continue;

      const sorted = [...txList].sort((a, b) => {
        const dayA = (a.date || a.created_at || '').slice(0, 10);
        const dayB = (b.date || b.created_at || '').slice(0, 10);
        if (dayA !== dayB) return dayA.localeCompare(dayB);
        const createA = new Date(a.created_at || a.date || 0).getTime();
        const createB = new Date(b.created_at || b.date || 0).getTime();
        return createA - createB;
      });

      const isCredit = acc.type === 'credit_card';
      let running = parseFloat(acc.initial_balance) || 0;
      if (isCredit && running > 0) {
        running = -running;
      }

      for (const tx of sorted) {
        const isAdj = tx.is_balance_adjustment || /ajuste de saldo/i.test(tx.description || '');
        if (isAdj) {
          let prev = running;
          let next = tx.new_balance;
          if ((next === undefined || next === null) && tx.notes) {
            const match = tx.notes.match(/(?:nuevo saldo|saldo despu[ée]s):\s*([-$0-9.,]+)/i);
            if (match) {
              next = parseFloat(match[1].replace(/[$]/g, ''));
            }
          }
          if (next !== undefined && !isNaN(next)) {
            const p = isCredit ? -Math.abs(prev) : prev;
            const n = isCredit ? -Math.abs(next) : next;
            map[tx.id] = { prev: p, next: n };
            running = n;
            continue;
          }
        }

        const prev = running;
        const amt = parseFloat(tx.amount) || 0;
        let next = prev;
        if (tx.is_retention && tx.retention_status === 'released') {
          next = prev;
        } else if (tx.is_breakdown) {
          next = prev;
        } else if (tx.type === 'income') {
          next = prev + amt;
        } else if (tx.type === 'expense') {
          next = prev - amt;
        }
        map[tx.id] = { prev, next };
        running = next;
      }
    }

    return map;
  }, [transactions, accounts]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      const matchesType = activeFilter === 'all' || t.type === activeFilter;
      const matchesSearch = !searchTerm || 
        (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (t.amount && t.amount.toString().includes(searchTerm));
      const matchesDate = isDateInFilter(t.date || t.created_at);
      const matchesAccount = selectedAccount === 'all' || t.account_id === selectedAccount;
      const matchesCategory = selectedCategory === 'all' || t.category_id === selectedCategory;
      return matchesType && matchesSearch && matchesDate && matchesAccount && matchesCategory;
    }).sort((a, b) => {
      const dayA = (a.date || a.created_at || '').slice(0, 10);
      const dayB = (b.date || b.created_at || '').slice(0, 10);
      if (dayA !== dayB) return dayB.localeCompare(dayA);
      const createA = new Date(a.created_at || a.date || 0).getTime();
      const createB = new Date(b.created_at || b.date || 0).getTime();
      return createB - createA;
    });
  }, [transactions, activeFilter, searchTerm, dateFilter, customStartDate, customEndDate, selectedAccount, selectedCategory]);

  const filteredSummary = useMemo(() => {
    const income = filteredTransactions.filter(t => t.type === 'income').reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
    const expense = filteredTransactions.filter(t => t.type === 'expense').reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
    const saving = filteredTransactions.filter(t => t.type === 'saving').reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
    return {
      count: filteredTransactions.length,
      income,
      expense,
      saving,
      balance: income - expense
    };
  }, [filteredTransactions]);

  const handleExport = (format) => {
    if (filteredTransactions.length === 0) {
      showToast.error('No hay transacciones para exportar con los filtros actuales');
      return;
    }
    if (!isPro) {
      showToast.error('La exportación de reportes está disponible en el Plan Pro');
      return;
    }

    try {
      if (format === 'pdf') {
        const periodLabel = dateFilter === 'this-month' ? 'Este Mes' :
                            dateFilter === 'last-month' ? 'Mes Pasado' :
                            dateFilter === 'this-year' ? 'Este Año' :
                            dateFilter === 'custom' ? `${customStartDate || 'Inicio'} a ${customEndDate || 'Hoy'}` : 'Historial Completo';
        exportToPDF(filteredTransactions, categories, periodLabel, null, userName);
        showToast.success('Reporte PDF generado exitosamente');
      } else if (format === 'excel') {
        const periodLabel = dateFilter === 'this-month' ? 'Este Mes' :
                            dateFilter === 'last-month' ? 'Mes Pasado' :
                            dateFilter === 'this-year' ? 'Este Año' :
                            dateFilter === 'custom' ? `${customStartDate || 'Inicio'} a ${customEndDate || 'Hoy'}` : 'Historial Completo';
        exportToExcel(filteredTransactions, categories, null, null, userName, periodLabel);
        showToast.success('Reporte Excel (.xls) generado exitosamente');
      } else if (format === 'csv') {
        exportToCSV(filteredTransactions, categories);
        showToast.success('Archivo CSV generado exitosamente');
      }
      setShowExportModal(false);
    } catch (err) {
      console.error('Error al exportar:', err);
      showToast.error('Error al generar la descarga');
    }
  };

  const groupByDate = (list) => {
    const sorted = [...list].sort((a, b) => {
      const dateA = a.date ? a.date.split('T')[0] : '';
      const dateB = b.date ? b.date.split('T')[0] : '';
      if (dateB !== dateA) return dateB.localeCompare(dateA);
      const timeA = new Date(a.created_at || a.date).getTime() || 0;
      const timeB = new Date(b.created_at || b.date).getTime() || 0;
      return timeB - timeA;
    });

    const groups = {};
    sorted.forEach(t => {
      const date = formatLocalDate(t.date, { month: 'short', day: 'numeric' });
      if (!groups[date]) groups[date] = [];
      groups[date].push(t);
    });
    return groups;
  };

  const grouped = groupByDate(filteredTransactions);
  const allItems = Object.values(grouped).flat();

  // Swipe hint: se dispara cada vez que se entra a la sección
  useEffect(() => {
    if (allItems.length === 0) return;
    const t = setTimeout(() => {
      setHintTriggered(true);
    }, 150);
    return () => clearTimeout(t);
  }, [allItems.length]);

  let firstItemRendered = false;

  return (
    <div 
      className="space-y-4 animate-page-enter"
      onClick={() => {
        if (revealedId) setRevealedId(null);
      }}
    >
      {/* Barra de búsqueda y herramientas (Filtros + Exportar) */}
      <div className="flex items-center gap-2 pt-0.5" onClick={() => { if (revealedId) setRevealedId(null); }}>
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar transacciones..."
            className="w-full h-12 pl-10 pr-8 bg-white border border-slate-200/80 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs font-medium"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Botón Filtros Avanzados */}
        <button
          type="button"
          onClick={() => setShowFilterModal(true)}
          className={`relative h-12 w-12 min-w-[48px] rounded-2xl border transition-all flex items-center justify-center shrink-0 active:scale-95 shadow-xs ${
            activeFiltersCount > 0
              ? 'bg-blue-50 border-blue-300 text-blue-600 font-bold'
              : 'bg-white border-slate-200/80 text-slate-600 hover:bg-slate-50'
          }`}
          title="Filtros avanzados"
          aria-label="Filtros avanzados"
        >
          <Filter className="w-4 h-4" />
          {activeFiltersCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white rounded-full text-[10px] font-black flex items-center justify-center shadow-xs animate-in zoom-in-50">
              {activeFiltersCount}
            </span>
          )}
        </button>

        {/* Botón Exportar */}
        <button
          type="button"
          onClick={() => setShowExportModal(true)}
          className="h-12 w-12 min-w-[48px] bg-white border border-slate-200/80 hover:bg-purple-50 text-purple-600 hover:border-purple-300 rounded-2xl transition-all flex items-center justify-center shrink-0 active:scale-95 shadow-xs relative"
          title="Exportar movimientos"
          aria-label="Exportar movimientos"
        >
          <Download className="w-4 h-4" />
          {!isPro && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-xs">
              <Lock className="w-2.5 h-2.5" />
            </span>
          )}
        </button>
      </div>

      {/* Píldoras de Filtros Activos (si hay alguno aplicado) */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap px-1 pt-0.5">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Activos:</span>
          {dateFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 shadow-xs">
              <Calendar className="w-3 h-3 text-blue-500" />
              {dateFilter === 'this-month' ? 'Este Mes' :
               dateFilter === 'last-month' ? 'Mes Pasado' :
               dateFilter === 'this-year' ? 'Este Año' : 'Rango Fechas'}
              <X className="w-3 h-3 cursor-pointer text-blue-400 hover:text-blue-700 ml-0.5" onClick={() => setDateFilter('all')} />
            </span>
          )}
          {selectedAccount !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 shadow-xs">
              <Wallet className="w-3 h-3 text-indigo-500" />
              <span className="max-w-[120px] truncate">{accounts.find(a => a.id === selectedAccount)?.name || 'Cuenta'}</span>
              <X className="w-3 h-3 cursor-pointer text-indigo-400 hover:text-indigo-700 ml-0.5" onClick={() => setSelectedAccount('all')} />
            </span>
          )}
          {selectedCategory !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-purple-50 text-purple-700 border border-purple-100 shadow-xs">
              <span className="max-w-[120px] truncate">{categories.find(c => c.id === selectedCategory)?.name || 'Categoría'}</span>
              <X className="w-3 h-3 cursor-pointer text-purple-400 hover:text-purple-700 ml-0.5" onClick={() => setSelectedCategory('all')} />
            </span>
          )}
          <button
            type="button"
            onClick={resetFilters}
            className="text-[11px] text-slate-500 hover:text-slate-800 font-bold underline px-1 active:scale-95 transition-transform"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {/* Chips de filtro */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none touch-pan-x" onClick={() => { if (revealedId) setRevealedId(null); }}>
        {filterChips.map(chip => {
          const isActive = activeFilter === chip.id;
          return (
            <button
              key={chip.id}
              onClick={() => setActiveFilter(chip.id)}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                isActive 
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 scale-105' 
                  : 'bg-white text-slate-600 border border-slate-100 hover:bg-slate-50 active:scale-95'
              }`}
            >
              {isActive && <Check className="w-3.5 h-3.5 stroke-[3px]" />}
              {chip.label}
            </button>
          );
        })}
      </div>

      {/* Hint label — visible brevemente */}
      {hintTriggered && (
        <p className="text-center text-[11px] text-slate-400 font-medium animate-pulse">
          ← Desliza para editar, duplicar o borrar
        </p>
      )}

      {/* Feed de Transacciones */}
      <div className="space-y-8">
        {Object.entries(grouped).map(([date, items]) => (
          <div key={date} className="space-y-3">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-2">{date}</h3>
            <div className="space-y-3">
              {items.map((t) => {
                const isFirst = !firstItemRendered;
                if (isFirst) firstItemRendered = true;
                return (
                  <SwipeableTransaction
                    key={t.id}
                    t={t}
                    isFirst={isFirst}
                    hintTriggered={hintTriggered}
                    categories={categories}
                    accounts={accounts}
                    transactionBalancesMap={transactionBalancesMap}
                    isRevealed={revealedId === t.id}
                    onReveal={() => setRevealedId(t.id)}
                    onCloseReveal={() => setRevealedId(null)}
                    onEdit={onEdit}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {transactions.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">No hay transacciones que mostrar</p>
          </div>
        )}
      </div>

      {/* ─── MODAL BOTTOM-SHEET: FILTROS AVANZADOS CON DRAG-DOWN ─── */}
      <MobileBottomSheet isOpen={showFilterModal} onClose={() => setShowFilterModal(false)}>
        {({ close }) => (
          <>
            {/* Cabecera */}
            <div className="flex items-center justify-between pb-1 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Filter className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-black text-slate-900">Filtros Avanzados</h3>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 1. Período / Fechas */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Período de Tiempo</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'all', label: 'Todo' },
                  { id: 'this-month', label: 'Este Mes' },
                  { id: 'last-month', label: 'Mes Pasado' },
                  { id: 'this-year', label: 'Este Año' },
                  { id: 'custom', label: 'Rango...' },
                ].map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setDateFilter(p.id)}
                    className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all ${
                      dateFilter === p.id
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-slate-50 text-slate-700 border-slate-200/80 hover:bg-slate-100'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Rango de Fechas Personalizado */}
              {dateFilter === 'custom' && (
                <div className="grid grid-cols-2 gap-2 pt-2 animate-in fade-in duration-200">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase">Desde</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={e => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-slate-50 text-slate-900 font-medium mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase">Hasta</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={e => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-slate-50 text-slate-900 font-medium mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 2. Cuenta Bancaria */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cuenta o Tarjeta</label>
              <select
                value={selectedAccount}
                onChange={e => setSelectedAccount(e.target.value)}
                className="w-full px-3 py-2.5 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas las cuentas</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} {acc.type === 'credit_card' ? '(Crédito)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* 3. Categoría */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Categoría</label>
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2.5 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas las categorías</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Pie de Acciones */}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold active:scale-95 transition-all"
              >
                Limpiar
              </button>
              <button
                type="button"
                onClick={close}
                className="flex-[2] py-3 px-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-lg shadow-blue-200 active:scale-95 transition-all"
              >
                Ver {filteredTransactions.length} {filteredTransactions.length === 1 ? 'movimiento' : 'movimientos'}
              </button>
            </div>
          </>
        )}
      </MobileBottomSheet>

      {/* ─── MODAL BOTTOM-SHEET: EXPORTAR MOVIMIENTOS CON DRAG-DOWN ─── */}
      <MobileBottomSheet isOpen={showExportModal} onClose={() => setShowExportModal(false)}>
        {({ close }) => (
          <>
            {/* Cabecera */}
            <div className="flex items-center justify-between pb-1 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                  <Download className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Exportar Reportes</h3>
                  <p className="text-[11px] text-slate-400">Descarga tus movimientos filtrados</p>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Resumen del Lote a Exportar */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 text-white rounded-2xl p-4 space-y-2.5 shadow-md">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Transacciones a exportar</span>
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs font-black rounded-full">
                  {filteredSummary.count} {filteredSummary.count === 1 ? 'movimiento' : 'movimientos'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800">
                <div>
                  <p className="text-[10px] text-emerald-400 font-bold uppercase">Ingresos</p>
                  <p className="text-sm font-black text-white">{formatCurrency(filteredSummary.income)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-rose-400 font-bold uppercase">Gastos</p>
                  <p className="text-sm font-black text-white">{formatCurrency(filteredSummary.expense)}</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 leading-tight pt-1">
                Filtros: {dateFilter !== 'all' ? (dateFilter === 'this-month' ? 'Este Mes' : dateFilter === 'last-month' ? 'Mes Pasado' : dateFilter === 'this-year' ? 'Este Año' : 'Personalizado') : 'Todo'}
                {selectedAccount !== 'all' ? ` · ${accounts.find(a => a.id === selectedAccount)?.name || 'Cuenta'}` : ''}
                {selectedCategory !== 'all' ? ` · ${categories.find(c => c.id === selectedCategory)?.name || 'Categoría'}` : ''}
              </p>
            </div>

            {/* Botones de Descarga */}
            <div className="space-y-2.5 pt-1">
              <div className="flex items-center justify-between px-1">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Elige el formato</p>
                {!isPro && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700">
                    <Lock className="w-3 h-3" /> Plan Pro
                  </span>
                )}
              </div>
              
              {/* 1. PDF */}
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    showToast.error('La exportación en PDF requiere el Plan Pro');
                    close();
                    navigate('/pricing');
                    return;
                  }
                  handleExport('pdf');
                }}
                disabled={filteredSummary.count === 0}
                className={`w-full flex items-center justify-between p-3.5 bg-white border rounded-2xl active:scale-[0.98] transition-all shadow-xs disabled:opacity-50 ${
                  isPro ? 'border-slate-200 hover:border-red-300 hover:bg-red-50/40' : 'border-purple-200/60 bg-purple-50/20 hover:bg-purple-50/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-900">Documento PDF</p>
                      {!isPro && <Lock className="w-3 h-3 text-purple-600" />}
                    </div>
                    <p className="text-xs text-slate-400">Reporte con resumen, firmas y tabla estilizada</p>
                  </div>
                </div>
                {isPro ? (
                  <Download className="w-4 h-4 text-red-600" />
                ) : (
                  <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                    <Lock className="w-3.5 h-3.5" />
                  </div>
                )}
              </button>

              {/* 2. Excel */}
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    showToast.error('La exportación a Excel requiere el Plan Pro');
                    close();
                    navigate('/pricing');
                    return;
                  }
                  handleExport('excel');
                }}
                disabled={filteredSummary.count === 0}
                className={`w-full flex items-center justify-between p-3.5 bg-white border rounded-2xl active:scale-[0.98] transition-all shadow-xs disabled:opacity-50 ${
                  isPro ? 'border-slate-200 hover:border-green-300 hover:bg-green-50/40' : 'border-purple-200/60 bg-purple-50/20 hover:bg-purple-50/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center shrink-0">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-900">Hoja de Cálculo Excel (.xls)</p>
                      {!isPro && <Lock className="w-3 h-3 text-purple-600" />}
                    </div>
                    <p className="text-xs text-slate-400">Tabla estructurada con diseño y colores</p>
                  </div>
                </div>
                {isPro ? (
                  <Download className="w-4 h-4 text-green-600" />
                ) : (
                  <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                    <Lock className="w-3.5 h-3.5" />
                  </div>
                )}
              </button>

              {/* 3. CSV */}
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    showToast.error('La exportación a CSV requiere el Plan Pro');
                    close();
                    navigate('/pricing');
                    return;
                  }
                  handleExport('csv');
                }}
                disabled={filteredSummary.count === 0}
                className={`w-full flex items-center justify-between p-3.5 bg-white border rounded-2xl active:scale-[0.98] transition-all shadow-xs disabled:opacity-50 ${
                  isPro ? 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/40' : 'border-purple-200/60 bg-purple-50/20 hover:bg-purple-50/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Download className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-900">Archivo Plano CSV</p>
                      {!isPro && <Lock className="w-3 h-3 text-purple-600" />}
                    </div>
                    <p className="text-xs text-slate-400">Valores delimitados por comas universal</p>
                  </div>
                </div>
                {isPro ? (
                  <Download className="w-4 h-4 text-blue-600" />
                ) : (
                  <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                    <Lock className="w-3.5 h-3.5" />
                  </div>
                )}
              </button>
            </div>

            {/* Banner promocional Pro para usuarios Free */}
            {!isPro && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-2xl text-center space-y-3 mt-1">
                <div className="w-10 h-10 bg-purple-600 text-white rounded-xl flex items-center justify-center mx-auto">
                  <Gem className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-purple-950">Función Exclusiva Plan Pro</h4>
                  <p className="text-xs text-purple-700 mt-0.5">
                    Exporta tus movimientos en PDF, Excel y CSV de forma ilimitada y accede a todas las herramientas avanzadas.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { close(); navigate('/pricing'); }}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs shadow-md shadow-purple-200 active:scale-95 transition-all flex items-center justify-center gap-1.5"
                >
                  <Lock className="w-3.5 h-3.5" />
                  <span>Ver Planes y Mejorar a Pro 🚀</span>
                </button>
              </div>
            )}
          </>
        )}
      </MobileBottomSheet>
    </div>
  );
};

export default MobileTransactions;
